import type { SalesOrderItem } from '@/lib/services/sales-orders/sales-orders.types'

/** A product line: its sales order item, nested or flat, and an id for keys when it has none. */
export interface ProductLineItemGroupSource {
  id?: number | string
  quantityId?: number | string
  sales_order_item_id?: number | null
  parent_sales_order_item_id?: number | null
  sales_order_item?: Pick<SalesOrderItem, 'id' | 'parent_sales_order_item_id'> | null
}

/** A root sales order item's lines, with the add-on lines whose parent it is. */
export interface ProductLineItemGroup<T extends ProductLineItemGroupSource> {
  key: string
  salesOrderItemId: number | null
  rootItems: T[]
  childItems: T[]
}

function getSalesOrderItemId(lineItem: ProductLineItemGroupSource): number | null {
  return lineItem?.sales_order_item?.id ?? lineItem?.sales_order_item_id ?? null
}

function getParentSalesOrderItemId(lineItem: ProductLineItemGroupSource): number | null {
  return (
    lineItem?.sales_order_item?.parent_sales_order_item_id ??
    lineItem?.parent_sales_order_item_id ??
    null
  )
}

export function getProductLineItemGroups<T extends ProductLineItemGroupSource>(
  productLineItems: T[] = []
): ProductLineItemGroup<T>[] {
  const rootGroups: ProductLineItemGroup<T>[] = []
  const rootGroupsBySalesOrderItemId = new Map<string, ProductLineItemGroup<T>>()
  const childrenByParentSalesOrderItemId = new Map<string, T[]>()

  productLineItems.forEach((lineItem, index) => {
    const parentSalesOrderItemId = getParentSalesOrderItemId(lineItem)

    if (parentSalesOrderItemId) {
      const parentKey = String(parentSalesOrderItemId)
      const siblings = childrenByParentSalesOrderItemId.get(parentKey) ?? []
      siblings.push(lineItem)
      childrenByParentSalesOrderItemId.set(parentKey, siblings)
      return
    }

    const salesOrderItemId = getSalesOrderItemId(lineItem)
    const key = salesOrderItemId ? `sales-order-item:${salesOrderItemId}` : `line-item:${index}`
    let group = salesOrderItemId ? rootGroupsBySalesOrderItemId.get(String(salesOrderItemId)) : null

    if (!group) {
      group = {
        key,
        salesOrderItemId,
        rootItems: [],
        childItems: [],
      }
      rootGroups.push(group)

      if (salesOrderItemId) {
        rootGroupsBySalesOrderItemId.set(String(salesOrderItemId), group)
      }
    }

    group.rootItems.push(lineItem)
  })

  childrenByParentSalesOrderItemId.forEach((childItems, parentKey) => {
    const parentGroup = rootGroupsBySalesOrderItemId.get(parentKey)

    if (parentGroup) {
      parentGroup.childItems.push(...childItems)
      return
    }

    childItems.forEach((lineItem) => {
      rootGroups.push({
        key: `orphan-child:${lineItem.quantityId ?? lineItem.id ?? rootGroups.length}`,
        salesOrderItemId: getSalesOrderItemId(lineItem),
        rootItems: [lineItem],
        childItems: [],
      })
    })
  })

  return rootGroups
}

export function getProductLineItemSalesOrderItemId(
  lineItem: ProductLineItemGroupSource
): number | null {
  return getSalesOrderItemId(lineItem)
}
